// src/components/auth/MfaCodeStep.jsx
// Login-time second step: password was right, Firebase demanded the second
// factor (auth/multi-factor-auth-required). Texts the code on mount and
// completes sign-in when the agent types it.
import { useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import CodeInput from "./CodeInput";
import {
  startMfaSignIn,
  finishMfaSignIn,
  mfaErrorMessage,
} from "../../utils/mfa";

export default function MfaCodeStep({ mfaError, onCancel, onSuccess }) {
  const auth = getAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null); // { resolver, verificationId }
  const recaptchaRef = useRef(null);
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    recaptchaRef.current = new RecaptchaVerifier(auth, "mfa-signin-recaptcha", {
      size: "invisible",
    });
    (async () => {
      try {
        const c = await startMfaSignIn(auth, mfaError, recaptchaRef.current);
        setChallenge(c);
        toast.success("Code texted to your phone.");
      } catch (err) {
        console.error("MFA challenge send failed:", err);
        toast.error(mfaErrorMessage(err));
        onCancel && onCancel();
      }
    })();
    return () => {
      try { recaptchaRef.current && recaptchaRef.current.clear(); } catch { /* fine */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    if (!challenge) return;
    if (code.length < 6) {
      toast.error("Enter the 6 digit code from the text.");
      return;
    }
    setBusy(true);
    try {
      const cred = await finishMfaSignIn(challenge.resolver, challenge.verificationId, code);
      // Signed in. The login form finishes the same post-auth steps as a
      // password-only login (user doc load, role check, navigate).
      onSuccess && onSuccess(cred);
    } catch (err) {
      console.error("MFA challenge confirm failed:", err);
      toast.error(mfaErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-lg font-bold text-slate-900 mb-1">Check your phone</h2>
      <p className="text-sm text-slate-500 mb-4">
        We texted a 6 digit code to your enrolled cell number.
      </p>
      <div id="mfa-signin-recaptcha" />
      <CodeInput value={code} onChange={setCode} onSubmit={confirm} />
      <button
        onClick={confirm}
        disabled={busy || !challenge}
        className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
      >
        {busy ? "Checking..." : "Verify and log in"}
      </button>
      <button onClick={onCancel} className="w-full text-sm text-slate-500 mt-3">
        Back to login
      </button>
    </div>
  );
}
